<template>
    <Modal ref="modal" class="add-missing-products-modal" @closed="onModalClose">
        <template #header="sp">
            <div class="h3">Marquer un produit comme manquant</div>
        </template>
        <template #body="sp">
            <div class="form-group row">
                <label for="staticCustomerName" class="col-4 col-form-label">{{ customerTypeLabel }}</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticCustomerName" :value="order.customer.name">
                </div>
            </div>

            <div class="form-group row">
                <label for="staticDeliveryDate" class="col-4 col-form-label">Date de livraison</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticDeliveryDate" :value="formatDate(order.delivery_date)">
                </div>
            </div>

            <div class="form-group row">
                <label for="staticProductName" class="col-4 col-form-label">Produit</label>
                <div class="col-8">
                    <input type="text" readonly class="form-control-plaintext" id="staticProductName" :value="productName">
                </div>
            </div>

            <div class="form-group row">
                <label for="quantity" class="col-4 col-form-label">Quantité manquante</label>
                <div class="col-8">
                    <input type="text" class="form-control" id="quantity" v-model="quantity" @click="onClickQuantity">
                </div>
            </div>

            <div class="form-group row">
                <label for="reason" class="col-4 col-form-label">Raison</label>
                <div class="col-8">
                    <select class="form-control" id="reason" v-model="reasonId">
                        <option v-if="order.final_customer_type === 'pickup_point'" :value="null">Non fabriqué</option>
                        <option v-for="reason in cancellationReasons" :value="reason.id">{{ reason.reason }}</option>
                    </select>
                </div>
            </div>

            <template v-if="order.paid_at">
                <div v-if="order.online" class="alert alert-warning">
                    Cette commande click & collect étant payée d'avance, vous devrez rembourser le client du montant correspondant au produit manquant.
                </div>
                <div v-else-if="mustConfirmRefund && !refundConfirmed" class="alert alert-danger">
                    Cette commande étant payée d'avance, vous devrez rembourser le client du montant correspondant au produit manquant.

                    <button class="btn btn-danger btn-block" @click="refundConfirmed = true">J'ai compris</button>
                </div>
            </template>

            <ErrorsZone :errors="errors" />
        </template>
        <template #footer>
            <button v-if="order.final_customer_type === 'pickup_point'" class="btn btn-warning" @click="onClickAddButtonWithoutAuthorization()">
                <i class="fas fa-plus"></i> Marquer manquant(s)
            </button>
            <AuthorizationModal
                v-else
                token-type="AddMissingProduct"
                :token-payload="authorizationTokenPayload"
                @authorised="onAuthorizationModalAuthorize"
            >
                <template #button="sp">
                    <AuthorizationModalButton :disabled="mustConfirmRefund && !refundConfirmed" class="btn-warning" @click="onClickAddButton(sp)">
                        Marquer manquant(s)
                    </AuthorizationModalButton>
                </template>
            </AuthorizationModal>
        </template>
    </Modal>
</template>

<script>
    import Modal from "../Modal";
    import ErrorsZone from "../ErrorsZone";
    import AuthorizationModal from "../AuthorizationModal";
    import AuthorizationModalButton from "../AuthorizationModalButton";

    import {usePostRequest} from "../compositions/axios/postRequest";

    export default {
        name: 'AddMissingProductsModal',

        components: {
            Modal,
            ErrorsZone,
            AuthorizationModal,
            AuthorizationModalButton,
        },

        setup() {
            const {
                loading: sending,
                errors,
                post
            } = usePostRequest();

            return {
                sending,
                errors,
                post
            };
        },

        props: {
            productSku: {
                type: String,
                required: true,
            },
            productName: {
                type: String,
                required: true,
            },
            order: {
                type: Object,
                required: true,
            },
            cancellationReasons: {
                type: Array,
                required: true,
            },
        },

        data() {
            return {
                quantity: 0,
                reasonId: null,

                refundConfirmed: false,
            };
        },

        watch: {
            quantity(newValue) {
                let qty = parseInt(newValue);
                if (isNaN(qty) || qty < 0) {
                    qty = 0;
                }
                this.quantity = qty;
            }
        },

        methods: {
            onClickQuantity(e) {
                e.target.select();
            },

            showModal() {
                this.$refs.modal.showModal();
            },

            closeModal() {
                this.$refs.modal.closeModal();
            },

            formatDate(date) {
                return moment(date).format('dddd D MMMM Y')
            },

            onModalClose() {
                this.quantity = 0;
                this.reasonId = null;
                this.refundConfirmed = false;
                this.errors = null;
            },

            onClickAddButton(sp) {
                if (this.validateFields()) {
                    sp.showModal();
                }
            },

            onClickAddButtonWithoutAuthorization() {
                if (this.validateFields()) {
                    this.sendRequest();
                }
            },

            validateFields() {
                this.errors = null;

                let errors = {};

                if (isNaN(this.quantity)) {
                    errors['quantity'] = ["La quantité est invalide. (nombre entier positif requis)"];
                } else if (this.quantity <= 0) {
                    errors['quantity'] = ["La quantité doit être supérieure à 0."];
                }

                if (!this.reasonId && this.order.final_customer_type !== 'pickup_point') {
                    errors['reason'] = ["La raison est obligatoire."];
                }

                if (Object.keys(errors).length > 0) {
                    this.errors = { message: "Merci de corriger les erreurs suivantes :", errors: errors };
                    return false;
                }

                return true;
            },

            async onAuthorizationModalAuthorize(token) {
                this.sendRequest(token);
            },

            async sendRequest(token) {
                const response = await this.post('/espace-employes/commandes/preparation/commande/' + this.order.id + '/produits-manquants', {
                    sku: this.productSku,
                    quantity: this.quantity,
                    reason: this.reasonId,
                    authorization_token: token ? token.token : null,
                });

                if (!this.errors) {
                    this.closeModal();

                    if (!this.$root.echoConnectedWihoutErrors) {
                        window.location.reload();
                    }
                }
            },
        },

        computed: {
            authorizationTokenPayload() {
                return {
                    order_id: this.order.id,
                    product_id: this.productSku,
                    quantity: this.quantity,
                    reason_id: this.reasonId,
                };
            },

            mustConfirmRefund() {
                return this.order.paid_at && !this.order.online;
            },

            customerTypeLabel() {
                switch (this.order.customer.type) {
                    case 'store':
                        return 'Magasin';
                    case 'short_channel':
                        return 'Circuit court';
                    case 'customer_group':
                        return 'Groupement de clients';
                    case 'pickup_point':
                        return 'Lieu de retrait';
                    default:
                        return 'Client';
                }
            },
        },
    }
</script>

<style lang="scss">
    .add-missing-products-modal {
        :first-of-type(.modal-container) {
            max-width: 600px;
            width: auto;
        }
    }
</style>
